import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import {
  BookOpen,
  ExternalLink,
  FileText,
  FlaskConical,
  Search,
  Users,
} from 'lucide-react';
import { ROUTES } from '../../constants';
import { db, firebaseReady } from '../../lib/firebase';
import { safeGetDoc } from '../../lib/firestoreAccess';
import { loadNewsPapers, searchPapers, type NewsPaperDoc } from '../../lib/newsPapers';
import { NewspaperSection } from '../../components/discover/NewspaperSection';
import { PostCard } from '../../components/feed/PostCard';
import { useAuth } from '../../hooks/useAuth';
import { useFilterModalDraft } from '../../hooks/useFilterModalDraft';
import { type FeedRow } from '../../hooks/useFeedItems';
import { DiscoverFilterFields } from '../../components/discover/DiscoverFilterFields';
import { FilterModal } from '../../components/shared/FilterModal';
import { ListPageFilterBar } from '../../components/shared/ListPageFilterBar';
import { sortPostsByTrending } from '../../lib/discoverScore';
import {
  DEFAULT_DISCOVER_FILTERS,
  clearDiscoverFilterChip,
  countDiscoverModalFilters,
  discoverFilterChips,
  filterDiscoverPosts,
  hasActiveDiscoverFilters,
  type DiscoverFilterChip,
} from '../../utils/discoverFilters';
import type { Lab, Post, User } from '../../types';

type DiscoverTab = 'trending' | 'resonated' | 'viewed' | 'following' | 'papers' | 'newspaper';

const TABS: { id: DiscoverTab; label: string }[] = [
  { id: 'trending',  label: 'Trending' },
  { id: 'resonated', label: 'Most resonated' },
  { id: 'viewed',    label: 'Most viewed' },
  { id: 'following', label: 'Following' },
  { id: 'newspaper', label: 'Newspaper' },
  { id: 'papers',    label: 'Papers' },
];

async function buildFeedRows(posts: Post[]): Promise<FeedRow[]> {
  if (!db || posts.length === 0) return posts.map(post => ({ post, author: null, lab: null }));
  const authorIds = Array.from(new Set(posts.map(p => p.authorId)));
  const labIds = Array.from(new Set(posts.map(p => p.labId).filter(Boolean) as string[]));
  const [authorSnaps, labSnaps] = await Promise.all([
    Promise.all(authorIds.map(id => safeGetDoc(doc(db!, 'users', id)))),
    Promise.all(labIds.map(id => safeGetDoc(doc(db!, 'labs', id)))),
  ]);
  const authorMap = new Map<string, User>();
  authorIds.forEach((id, i) => {
    const s = authorSnaps[i];
    if (s?.exists()) authorMap.set(id, { uid: id, ...(s.data() as Omit<User, 'uid'>) });
  });
  const labMap = new Map<string, Lab>();
  labIds.forEach((id, i) => {
    const s = labSnaps[i];
    if (s?.exists()) labMap.set(id, { id, ...(s.data() as Omit<Lab, 'id'>) });
  });
  return posts.map(post => ({
    post,
    author: authorMap.get(post.authorId) ?? null,
    lab: post.labId ? labMap.get(post.labId) ?? null : null,
  }));
}

// ── arXiv paper field colours ────────────────────────────────────────────────
const FIELD_COLORS: Record<string, string> = {
  'AI / ML':      'bg-violet-500/15 text-violet-400 ring-violet-500/25',
  'Physics':      'bg-blue-500/15 text-blue-400 ring-blue-500/25',
  'Mathematics':  'bg-amber-500/15 text-amber-400 ring-amber-500/25',
  'Biology':      'bg-emerald-500/15 text-emerald-400 ring-emerald-500/25',
  'Neuroscience': 'bg-pink-500/15 text-pink-400 ring-pink-500/25',
  'Economics':    'bg-orange-500/15 text-orange-400 ring-orange-500/25',
  'Statistics':   'bg-cyan-500/15 text-cyan-400 ring-cyan-500/25',
};
function fieldBadgeClass(field: string) {
  return FIELD_COLORS[field] ?? 'bg-surface-raised text-fg-muted ring-border';
}

function pdfUrl(abstractUrl: string) {
  return abstractUrl.replace('arxiv.org/abs/', 'arxiv.org/pdf/');
}

// ── Single arXiv paper card ──────────────────────────────────────────────────
function PaperRow({ paper }: { paper: NewsPaperDoc }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className="rounded-card border border-border bg-surface-card p-4 transition hover:border-brand/30">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ${fieldBadgeClass(paper.researchArea)}`}>
              <FlaskConical size={9} strokeWidth={2} />{paper.researchArea}
            </span>
            {paper.isOpenAccess && (
              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400 ring-1 ring-emerald-500/20">
                Open Access
              </span>
            )}
            <span className="text-[11px] tabular-nums text-fg-subtle">{paper.publishedAt}</span>
          </div>
          <a
            href={paper.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-start gap-1 font-medium leading-snug text-fg hover:text-brand"
          >
            <span className="flex-1">{paper.title}</span>
            <ExternalLink size={13} strokeWidth={1.5} className="mt-0.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />
          </a>
          {paper.authors.length > 0 && (
            <div className="flex items-center gap-1.5 text-[11px] text-fg-muted">
              <Users size={11} strokeWidth={1.5} className="shrink-0" />
              <span className="truncate">
                {paper.authors.slice(0, 4).join(', ')}{paper.authors.length > 4 ? ` +${paper.authors.length - 4} more` : ''}
              </span>
            </div>
          )}
          {paper.abstract && (
            <div>
              <p className={`text-xs leading-relaxed text-fg-subtle ${expanded ? '' : 'line-clamp-2'}`}>
                {paper.abstract}
              </p>
              {paper.abstract.length > 160 && (
                <button
                  onClick={() => setExpanded(e => !e)}
                  className="mt-0.5 text-[11px] text-brand hover:underline"
                >
                  {expanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="text-right">
            <span className="text-sm font-bold tabular-nums text-brand">{paper.score.toFixed(0)}</span>
            <p className="text-[9px] text-fg-subtle">score</p>
          </div>
          <a
            href={pdfUrl(paper.url)}
            target="_blank"
            rel="noopener noreferrer"
            title="Open PDF"
            className="flex items-center gap-1 rounded-md border border-border bg-surface-raised px-2 py-1 text-[10px] font-medium text-fg-muted transition hover:border-brand/40 hover:text-brand"
          >
            <FileText size={11} strokeWidth={1.5} />
            PDF
          </a>
        </div>
      </div>
    </li>
  );
}

// ── Papers tab content ───────────────────────────────────────────────────────
function PapersSection() {
  const [papers, setPapers] = useState<NewsPaperDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const rows = await loadNewsPapers({ maxCount: 1000 });
      if (!cancelled) { setPapers(rows); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const displayed = useMemo(() => searchPapers(papers, search), [papers, search]);

  if (loading) {
    return (
      <div className="space-y-3" aria-busy="true">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-24 animate-pulse rounded-card border border-border bg-surface-raised/40" />
        ))}
      </div>
    );
  }

  if (papers.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-card border border-border bg-surface-card py-16 text-center">
        <BookOpen size={32} strokeWidth={1} className="text-fg-subtle" />
        <p className="text-sm text-fg-muted">
          No papers yet. Go to <Link to={ROUTES.arxivTest} className="text-brand hover:underline">arXiv Test</Link> and click <strong>Fetch Papers</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={15} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle" />
        <input
          type="text"
          placeholder="Search by title, author, institution, field…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full rounded-lg border border-border bg-surface-card py-2 pl-9 pr-4 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand/40"
        />
      </div>

      <p className="text-xs text-fg-subtle">
        {displayed.length}{search ? ` of ${papers.length}` : ''} papers
        {search ? ` matching "${search}"` : ' · sorted by relevance score'}
      </p>

      {displayed.length === 0 && (
        <p className="text-sm text-fg-muted">No papers match "{search}".</p>
      )}

      <ul className="space-y-2">
        {displayed.map(p => <PaperRow key={p.id} paper={p} />)}
      </ul>

      <p className="text-center text-[10px] italic text-fg-subtle">
        "Thank you to arXiv for use of its open access interoperability."
      </p>
    </div>
  );
}

// ── Main Discover page ───────────────────────────────────────────────────────
export function DiscoverPage() {
  const { profile } = useAuth();
  const [tab, setTab] = useState<DiscoverTab>('trending');
  const [filters, setFilters] = useState(DEFAULT_DISCOVER_FILTERS);
  const filterModal = useFilterModalDraft(filters);
  const [rows, setRows] = useState<FeedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPublic = useCallback(async () => {
    if (tab === 'papers' || tab === 'newspaper') return;
    if (!firebaseReady || !db) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const base = collection(db, 'posts');
      let snap;
      if (tab === 'following') {
        if (!profile?.following?.length) {
          setRows([]);
          setLoading(false);
          return;
        }
        const ids = profile.following.slice(0, 25);
        const qy = query(
          base,
          where('authorId', 'in', ids),
          orderBy('createdAt', 'desc'),
          limit(40)
        );
        snap = await getDocs(qy);
      } else {
        const qy = query(
          base,
          where('visibility', '==', 'public'),
          orderBy('createdAt', 'desc'),
          limit(100)
        );
        snap = await getDocs(qy);
      }
      let posts: Post[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<Post, 'id'>),
      }));
      if (filters.field) {
        posts = posts.filter((p) => p.researchArea === filters.field);
      }
      if (tab === 'resonated') {
        posts = [...posts].sort((a, b) => b.resonateCount - a.resonateCount);
      } else if (tab === 'viewed') {
        posts = [...posts].sort((a, b) => b.viewCount - a.viewCount);
      } else if (tab === 'trending') {
        posts = sortPostsByTrending(posts);
      }
      const feedRows = await buildFeedRows(posts.slice(0, 40));
      setRows(feedRows);
    } catch {
      setError('Could not load discover feed.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab, filters.field, profile?.following]);

  useEffect(() => {
    void loadPublic();
  }, [loadPublic]);

  const displayedRows = useMemo(() => {
    const filtered = filterDiscoverPosts(rows.map(r => r.post), filters);
    const ids = new Set(filtered.map(p => p.id));
    return rows.filter(r => ids.has(r.post.id));
  }, [rows, filters]);
  const chips = useMemo(
    () => discoverFilterChips(filters).map((c) => ({ id: c.id, label: c.label })),
    [filters]
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl text-fg">Discover</h1>
        <p className="mt-2 text-sm text-fg-muted">
          Public research updates and the latest academic papers.
        </p>
      </div>

      <Link
        to={ROUTES.brief}
        className="flex items-center justify-between rounded-card border border-brand/40 bg-brand/10 px-4 py-3 text-sm text-fg transition hover:bg-brand/15"
      >
        <span>
          <span className="font-medium text-brand">Weekly brief</span>
          <span className="text-fg-muted"> — curated snapshot (beta)</span>
        </span>
        <span className="text-brand" aria-hidden>→</span>
      </Link>

      {tab !== 'papers' && tab !== 'newspaper' && (
        <ListPageFilterBar
          searchId="disc-search"
          search={filters.query}
          onSearchChange={(query) => setFilters((f) => ({ ...f, query }))}
          searchPlaceholder="Search titles and content…"
          onOpenFilters={() => filterModal.setOpen(true)}
          activeFilterCount={countDiscoverModalFilters(filters)}
          chips={chips}
          onRemoveChip={(id) =>
            setFilters((f) => clearDiscoverFilterChip(f, id as DiscoverFilterChip['id']))
          }
          onClearAll={
            hasActiveDiscoverFilters(filters) ? () => setFilters(DEFAULT_DISCOVER_FILTERS) : undefined
          }
          resultSummary={!loading ? `${displayedRows.length} posts` : undefined}
        />
      )}

      <FilterModal
        open={filterModal.open}
        title="Filter discover"
        subtitle="Research field reloads the feed; post type filters the current list."
        onClose={filterModal.close}
        onApply={() => filterModal.apply(setFilters)}
        onReset={() => filterModal.setDraft(DEFAULT_DISCOVER_FILTERS)}
      >
        <DiscoverFilterFields
          filters={filterModal.draft}
          onChange={filterModal.setDraft}
        />
      </FilterModal>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1 border-b border-border pb-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              tab === t.id
                ? 'bg-surface-raised text-fg'
                : 'text-fg-muted hover:bg-surface-raised/60'
            }`}
          >
            {t.label}
            {t.id === 'newspaper' && (
              <span className="rounded-full bg-brand/15 px-1.5 py-0.5 text-[9px] font-semibold text-brand">
                7d
              </span>
            )}
            {t.id === 'papers' && (
              <span className="rounded-full bg-brand/15 px-1.5 py-0.5 text-[9px] font-semibold text-brand">
                arXiv
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Newspaper tab */}
      {tab === 'newspaper' && <NewspaperSection />}

      {/* Papers tab */}
      {tab === 'papers' && <PapersSection />}

      {/* Social posts tabs */}
      {tab !== 'papers' && tab !== 'newspaper' && (
        <>
          {tab === 'following' && (!profile?.following || profile.following.length === 0) && (
            <p className="text-sm text-fg-muted">
              Follow researchers from their profiles to see their public posts here.
            </p>
          )}

          {error && (
            <p className="rounded-card border border-red-500/40 bg-red-950/30 px-3 py-2 text-sm text-red-200">
              {error}
            </p>
          )}

          {loading && (
            <div className="space-y-3" aria-busy="true">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-24 animate-pulse rounded-card border border-border bg-surface-raised/40" />
              ))}
            </div>
          )}

          {!loading && displayedRows.length === 0 && !error && (
            <p className="text-sm text-fg-muted">No posts match these filters yet.</p>
          )}

          <ul className="space-y-3">
            {!loading && displayedRows.map((row) => (
              <li key={row.post.id}>
                <PostCard row={row} viewerUid={profile?.uid} onChanged={loadPublic} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
